import './Combobox.scss'
import { useId, useMemo, useRef, useState, type ComponentProps, type ReactNode } from 'react'
import { MdOutlineClose, MdOutlineExpandMore } from 'react-icons/md'
import { clx } from '../clx'
import { IconButton } from '../IconButton'
import {
  assignRef,
  buildModel,
  defaultFilter,
  ListboxPopup,
  optionId,
  useListbox,
  type ListboxFilter,
  type ListboxOption,
  type ListboxOptionState
} from '../Listbox'

export type ComboboxOption = ListboxOption
export type ComboboxOptionState = ListboxOptionState

export interface ComboboxProps extends Omit<
  ComponentProps<'input'>,
  'value' | 'defaultValue' | 'onChange' | 'size' | 'type' | 'children'
> {
  options: readonly ComboboxOption[]
  value: string | null
  onChange: (value: string | null) => void
  /** Match an option against the typed query. Default: case-insensitive substring of `textValue ?? label`. */
  filter?: ListboxFilter
  /** Custom option content (e.g. a stage dot). Rows must stay one line high. */
  renderOption?: (option: ComboboxOption, state: ComboboxOptionState) => ReactNode
  /** Show a clear button while a value is selected. */
  isClearable?: boolean
  /** Options are being fetched: the popup shows a spinner. */
  isLoading?: boolean
  emptyText?: ReactNode
  isDisabled?: boolean
  isReadOnly?: boolean
  size?: 'sm' | 'md'
}

/**
 * Autocomplete (WAI-ARIA 1.2 combobox with a listbox popup). Focus stays in
 * the input; the active option is announced through aria-activedescendant.
 * Long lists are windowed, so 25 000 options stay fast.
 */
export function Combobox({
  options,
  value,
  onChange,
  filter = defaultFilter,
  renderOption,
  isClearable = false,
  isLoading = false,
  emptyText = 'No results',
  isDisabled = false,
  isReadOnly = false,
  size = 'md',
  disabled,
  readOnly,
  className,
  ref,
  onKeyDown,
  onBlur,
  onClick,
  ...rest
}: ComboboxProps) {
  const listId = `combobox-${useId()}-listbox`
  const inputRef = useRef<HTMLInputElement | null>(null)
  const anchorRef = useRef<HTMLDivElement>(null)
  /** The typed text; null shows the selected option's label. */
  const [query, setQuery] = useState<string | null>(null)

  const model = useMemo(() => buildModel(options, query ?? '', filter), [options, query, filter])
  const selected = useMemo(
    () => (value === null ? undefined : options.find((o) => o.value === value)),
    [options, value]
  )
  const isInteractive = !(isDisabled || disabled || isReadOnly || readOnly)

  const listbox = useListbox({
    model,
    selectedIndex: value === null ? -1 : (model.indexOf.get(value) ?? -1),
    isAutoActive: Boolean(query),
    onPick: (option) => {
      onChange(option.value)
      setQuery(null)
    }
  })

  const dismiss = () => {
    listbox.close()
    setQuery(null)
  }

  const clear = () => {
    onChange(null)
    setQuery(null)
    inputRef.current?.focus()
  }

  return (
    <div
      ref={anchorRef}
      className={clx('combobox', size !== 'md' && `combobox--is-${size}`, className)}
      data-disabled={isDisabled || disabled || undefined}
    >
      <input
        {...rest}
        ref={(node) => {
          inputRef.current = node
          assignRef(ref, node)
        }}
        type="text"
        role="combobox"
        className="combobox__input"
        autoComplete="off"
        spellCheck={false}
        disabled={isDisabled || disabled}
        readOnly={isReadOnly || readOnly}
        aria-autocomplete="list"
        aria-expanded={listbox.isOpen}
        aria-controls={listbox.isOpen ? listId : undefined}
        aria-activedescendant={
          listbox.isOpen && listbox.activeIndex >= 0
            ? optionId(listId, listbox.activeIndex)
            : undefined
        }
        value={query ?? selected?.label ?? ''}
        onChange={(event) => {
          if (!isInteractive) return
          setQuery(event.target.value)
          if (!listbox.isOpen) listbox.open()
          listbox.resetActive()
        }}
        onClick={(event) => {
          onClick?.(event)
          if (isInteractive && !listbox.isOpen) listbox.open()
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if (event.defaultPrevented || !isInteractive) return
          if (event.key === 'Escape') {
            // Open: the Escape stack closes the popup. Closed: drop the typed text.
            if (!listbox.isOpen && query !== null) {
              event.preventDefault()
              setQuery(null)
            }
            return
          }
          if (event.key === 'Tab') setQuery(null)
          listbox.onKeyDown(event)
        }}
        onBlur={(event) => {
          onBlur?.(event)
          if (anchorRef.current?.contains(event.relatedTarget)) return
          dismiss()
        }}
      />
      {isClearable && value !== null && isInteractive && (
        <IconButton
          label="Clear"
          icon={<MdOutlineClose />}
          size="sm"
          hasTooltip={false}
          className="combobox__clear"
          onClick={clear}
        />
      )}
      <span
        className="combobox__chevron"
        aria-hidden="true"
        onMouseDown={(event) => {
          event.preventDefault()
          if (!isInteractive) return
          inputRef.current?.focus()
          if (listbox.isOpen) listbox.close()
          else listbox.open()
        }}
      >
        <MdOutlineExpandMore />
      </span>
      {listbox.isOpen && (
        <ListboxPopup
          id={listId}
          anchorRef={anchorRef}
          inputRef={inputRef}
          model={model}
          activeIndex={listbox.activeIndex}
          isSelected={(option) => option.value === value}
          onPick={listbox.pick}
          onActiveChange={listbox.setActiveIndex}
          onDismiss={dismiss}
          renderOption={renderOption}
          isLoading={isLoading}
          emptyText={emptyText}
          size={size}
        />
      )}
    </div>
  )
}
